import * as echarts from "echarts";
import type { EChartsOption } from "echarts";
import { useEffect, useMemo, useRef } from "react";

import type { EntryDistributionItem, TrendDataPoint } from "@zhihaoscm/entity";
import { FUNCTION_MODULE } from "@zhihaoscm/meta";
import useChart from "../../../../utils/useChart";

const getChartInterval = (values: number[], splitNumber = 5) => {
    const interval = values.length ? Math.ceil(Math.max(...values, 0) / splitNumber) : 0;
    return { interval, max: interval * splitNumber };
};

const { getLegendIcon } = useChart();

interface FunctionalTrendTooltipParam {
    axisValue?: string | number;
    seriesName?: string;
    value?: unknown;
}

/** 复用旧 Vue 趋势图 tooltip 的系列图标，避免退回 ECharts 默认标记。 */
const getFunctionalTrendTooltipIcon = (type: "bar" | "line", color: string) => {
    if (type === "bar") {
        return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="4" width="8" height="8" fill="${color}"/></svg>`;
    }

    return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="7" width="16" height="2" fill="${color}"/><path opacity="0.25" d="M4 4C6.20914 1.79086 9.79086 1.79086 12 4C14.2091 6.20914 14.2091 9.79086 12 12C9.79086 14.2091 6.20914 14.2091 4 12C1.79086 9.79086 1.79086 6.20914 4 4Z" fill="${color}"/><path d="M5.85352 5.85352C7.03897 4.66807 8.96103 4.66807 10.1465 5.85352C11.3319 7.03897 11.3319 8.96103 10.1465 10.1465C8.96103 11.3319 7.03897 11.3319 5.85352 10.1465C4.66807 8.96103 4.66807 7.03897 5.85352 5.85352Z" fill="${color}" stroke="white"/></svg>`;
};

/** 生成与旧 Vue 一致的趋势图 tooltip HTML。 */
const formatFunctionalTrendTooltip = (params: unknown) => {
    const normalizedParams = (Array.isArray(params) ? params : [params]) as FunctionalTrendTooltipParam[];
    let html = `<div class="tooltip-title">${normalizedParams[0]?.axisValue ?? ""}</div>`;

    normalizedParams.forEach(param => {
        const icon =
            param.seriesName === "访问次数"
                ? getFunctionalTrendTooltipIcon("bar", "#1777FF")
                : param.seriesName === "访问人数"
                  ? getFunctionalTrendTooltipIcon("line", "#FFAE4C")
                  : "";

        html += `<div class="tooltip-item">${icon ? `<span class="tooltip-icon">${icon}</span>` : ""}<span class="tooltip-item-label">${param.seriesName ?? ""}</span><span class="tooltip-item-value" >${param.value ?? ""}</span></div>`;
    });

    return html;
};

/** 生成旧 Vue PV/UV 图表配置，保留双 Y 轴和柱线组合展示。 */
export function buildFunctionalTrendChartOption(data: TrendDataPoint[] = []): EChartsOption {
    const pv = data.map(item => item.value);
    const uv = data.map(item => item.userValue);
    const pvAxis = getChartInterval(pv);
    const uvAxis = getChartInterval(uv);

    return {
        grid: { left: 5, right: 5, bottom: "20%", top: "13%", containLabel: true },
        legend: {
            data: [
                { name: "访问次数", icon: getLegendIcon("bar", "1777FF") },
                { name: "访问人数", icon: getLegendIcon("line", "FFAE4C") }
            ],
            textStyle: {
                fontSize: 14,
                lineHeight: 14,
                rich: { a: { verticalAlign: "middle" } },
                padding: [0, 0, -2, 0]
            },
            itemWidth: 14,
            itemHeight: 14,
            bottom: 0,
            left: "center"
        },
        tooltip: {
            trigger: "axis",
            padding: [8, 12],
            className: "functional-operation-trend-tooltip",
            formatter: formatFunctionalTrendTooltip
        },
        xAxis: [
            {
                type: "category",
                data: data.map(item => item.timeLabel),
                axisTick: { show: false },
                axisLine: { lineStyle: { color: "#F0F0F0" } },
                axisLabel: { color: "#000", fontSize: 12 }
            }
        ],
        yAxis: [
            {
                type: "value",
                name: "次",
                min: 0,
                max: pvAxis.max,
                interval: pvAxis.interval,
                splitNumber: 5,
                axisLine: { show: true, lineStyle: { color: "#F0F0F0" } },
                axisLabel: { color: "#000", fontSize: 12 },
                nameTextStyle: { color: "#000", fontSize: 12, padding: [0, 27, 0, 0] }
            },
            {
                type: "value",
                name: "人",
                min: 0,
                max: uvAxis.max,
                interval: uvAxis.interval,
                splitNumber: 5,
                axisLine: { show: true, lineStyle: { color: "#F0F0F0" } },
                axisLabel: { color: "#000", fontSize: 12 },
                nameTextStyle: { color: "#000", fontSize: 12, align: "left", padding: [0, 0, 0, 7] }
            }
        ],
        series: [
            {
                name: "访问次数",
                type: "bar",
                yAxisIndex: 0,
                barWidth: 12,
                itemStyle: { color: "#1777FF", borderRadius: 2 },
                data: pv
            },
            {
                name: "访问人数",
                type: "line",
                yAxisIndex: 1,
                smooth: true,
                showSymbol: false,
                silent: true,
                lineStyle: { color: "#FFAE4C", width: 3 },
                itemStyle: { color: "#FFAE4C" },
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: "rgba(255,174,76,0.28)" },
                        { offset: 0.7, color: "rgba(255,174,76,0.1)" },
                        { offset: 1, color: "transparent" }
                    ]),
                    shadowBlur: 4,
                    shadowColor: "#FFB916"
                },
                data: uv
            }
        ]
    };
}

/** 生成旧 Vue 入口横向柱状图配置，保持入口名称与访问次数同序。 */
export function buildFunctionalEntryChartOption(data: EntryDistributionItem[] = []): EChartsOption {
    return {
        grid: { left: 8, right: 10, top: 38, bottom: 6, containLabel: true },
        tooltip: { trigger: "axis", axisPointer: { type: "cross" } },
        xAxis: {
            type: "value",
            boundaryGap: [0, 0.1],
            splitLine: { lineStyle: { type: "dashed", color: "#D9D9D9" } },
            axisLabel: { color: "#000", fontSize: 12 }
        },
        yAxis: {
            type: "category",
            data: data.map(item => item.entryName),
            axisLine: { lineStyle: { color: "#D9D9D9" } },
            axisLabel: { color: "#000", fontSize: 12 }
        },
        series: [
            {
                type: "bar",
                data: data.map(item => item.visitCount),
                barWidth: 14,
                itemStyle: { color: "#0BBB7D", borderRadius: 4 },
                label: {
                    show: true,
                    position: "right",
                    color: "#0BBB7D",
                    fontSize: 12,
                    fontWeight: 700,
                    formatter: (params: { value?: unknown }) => (params.value === 0 ? "" : `${params.value ?? ""}`)
                }
            }
        ]
    };
}

interface ChartClickParams {
    componentType?: string;
    seriesType?: string;
    name?: string;
}

interface FunctionalTrendChartProps {
    data?: TrendDataPoint[];
    onFunctionTypeChange?: (value: number) => void;
}

/** React ECharts PV/UV 图表，柱状图点击时沿用 Vue 的功能模块切换事件。 */
export function FunctionalOperationTrendChart({ data = [], onFunctionTypeChange }: FunctionalTrendChartProps) {
    const chartRef = useRef<HTMLDivElement>(null);
    const option = useMemo(() => buildFunctionalTrendChartOption(data), [data]);

    useEffect(() => {
        const element = chartRef.current;
        // 容器尚未完成布局时不初始化 ECharts，避免零尺寸 canvas 产生运行时异常。
        if (!element || element.clientWidth === 0 || element.clientHeight === 0) return;
        const chart = echarts.init(element);
        const handleClick = (params: ChartClickParams) => {
            if (params.componentType !== "series" || params.seriesType !== "bar" || !params.name) return;
            const module = FUNCTION_MODULE.options.find(item => item.label === params.name);
            if (module) onFunctionTypeChange?.(Number(module.value));
        };
        chart.setOption(option);
        chart.on("click", handleClick);
        const handleResize = () => chart.resize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            chart.dispose();
        };
    }, [onFunctionTypeChange, option]);

    // 当前路由旧 Vue pvUvChart 容器的实际高度为 287px，保持运行时高度避免上下模块错位。
    return <div ref={chartRef} aria-label="功能运营趋势图" className="h-[287px] w-full" />;
}

/** React ECharts 入口分布图，保留旧 Vue 横向柱状图展示方式。 */
export function FunctionalOperationEntryChart({ data = [] }: { data?: EntryDistributionItem[] }) {
    const chartRef = useRef<HTMLDivElement>(null);
    const option = useMemo(() => buildFunctionalEntryChartOption(data), [data]);

    useEffect(() => {
        const element = chartRef.current;
        // 容器尚未完成布局时不初始化 ECharts，避免零尺寸 canvas 产生运行时异常。
        if (!element || element.clientWidth === 0 || element.clientHeight === 0) return;
        const chart = echarts.init(element);
        chart.setOption(option);
        const handleResize = () => chart.resize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            chart.dispose();
        };
    }, [option]);

    // 当前路由旧 Vue entranceChart 容器的实际高度为 375px，保持入口统计卡片几何尺寸。
    return <div ref={chartRef} aria-label="入口访问量图" className="h-[375px] w-full" />;
}
